import {
  getPriceReferenceLabel,
  isPriceReferenceParamVisible,
  userCanSeeEndCustomer,
  userCanSeePriceReference,
} from "./CartPage/LineItemStaticParams";
import PriceReferenceDropdown from "../../ProductDetails/components/AddToCartCard/PriceReferenceDropdown";
import classNames from "classnames";
import { type ProductData } from "../../models/ProductData";
import { type LineItem } from "../models/CartData";
import Parameter from "../../common/components/Parameter";
import { useTranslation } from "react-i18next";

type BaseParamProps = JSX.IntrinsicElements["div"] & {
  className?: string;
  product: ProductData;
  lineItem: LineItem;
  "data-test-id"?: string;
  children?: never;
};

interface PriceReferenceParamProps extends Omit<BaseParamProps, "onChange" | "lineItem" | "product"> {
  product: ProductData | null;
  priceReference: string;
  onChange?: (priceRef: string | undefined) => void;
  disabled?: boolean;
  editable?: boolean;
  hideLabel?: boolean;
  externalLabel?: boolean;
  size?: "small" | "medium";
}

export const PriceReferenceParam = ({
  onChange,
  disabled,
  product,
  priceReference,
  "data-test-id": dataTestId,
  editable,
  hideLabel = false,
  externalLabel,
  size,
  ...rest
}: PriceReferenceParamProps) => {
  const { t } = useTranslation();

  if (!isPriceReferenceParamVisible()) {
    return null;
  }

  if (userCanSeeEndCustomer()) {
    return (
      <Parameter label={t("productDetailPage.endCustomerLabel")} cutOverflow={true} {...rest} data-test-id={dataTestId}>
        {getPriceReferenceLabel(priceReference)}
      </Parameter>
    );
  } else if (userCanSeePriceReference()) {
    if (editable) {
      return (
        <div {...rest} className={classNames("w-100", rest.className)}>
          <PriceReferenceDropdown
            data-test-id={dataTestId}
            selected={priceReference}
            onChange={onChange}
            disabled={disabled}
            hideLabel={hideLabel}
            externalLabel={externalLabel}
            size={size}
          />
        </div>
      );
    } else {
      return (
        <Parameter
          label={t("component.dropdown.label.priceReference")}
          cutOverflow={true}
          {...rest}
          data-test-id={dataTestId}
        >
          {getPriceReferenceLabel(priceReference)}
        </Parameter>
      );
    }
  }

  return null;
};
